import { assert, CheckedAddress, NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'
import { useQuery } from '@tanstack/react-query'
import z from 'zod'

import { SpkFarmGeneralStatsResult } from '../types'
import { getChainConfigEntry } from '@/config/chain'
import { infoSkyApiUrl } from '@/config/consts'
import { normalizedNumberSchema, percentageAboveOneSchema } from '@/domain/common/validation'

export interface UseSpkFarmGeneralStatsParams {
  chainId: number
}

export function useSpkFarmGeneralStats({ chainId }: UseSpkFarmGeneralStatsParams): SpkFarmGeneralStatsResult {
  const { spk } = getChainConfigEntry(chainId)
  assert(spk, 'Spk feature config is not defined on this chain')

  return useQuery({
    queryKey: ['spk-farm-general-stats', chainId],
    queryFn: () => getBAFarmData({ farmAddress: spk.farmConfig.address }),
    select: (data) => {
      return {
        stakers: data.depositors,
        tvl: data.totalStaked,
        apr: data.apy,
      }
    },
  })
}

interface GetFarmBADataParams {
  farmAddress: CheckedAddress
}

interface GetFarmBADataResult {
  apy: Percentage
  depositors: number
  rewardTokenPriceUsd: NormalizedNumber
  totalRewarded: NormalizedNumber
  totalStaked: NormalizedNumber
}

async function getBAFarmData({ farmAddress }: GetFarmBADataParams): Promise<GetFarmBADataResult> {
  const res = await fetch(`${infoSkyApiUrl}/farms/${farmAddress.toLowerCase()}/`)
  if (!res.ok) {
    throw new Error(`Failed to fetch farm data: ${res.statusText}`)
  }

  return baFarmDataResponseSchema.parse(await res.json())
}

export const baFarmDataResponseSchema = z
  .object({
    apy: percentageAboveOneSchema,
    depositors: z.number(),
    price: normalizedNumberSchema,
    total_farmed: normalizedNumberSchema,
    total_staked: normalizedNumberSchema,
  })
  .transform(({ apy, depositors, price, total_farmed, total_staked }) => ({
    apy,
    depositors,
    rewardTokenPriceUsd: price,
    totalRewarded: total_farmed,
    totalStaked: total_staked,
  }))
